import React, { useCallback, useMemo } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { Box, withCitrusTheme } from '@gousto-internal/citrus-react'
import { useDispatch } from 'react-redux'

import { CampaignBanner, CompactCampaignBanner } from '@library/menu-page'
import type { CollectionUUID, Recipe, RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useCollections, useCurrentCollectionId, useSelectCollection } from 'hooks/menu-collections'
import { useMenuMeta } from 'hooks/menu-metadata/useMenuMeta'
import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import type { SelectCollectionEvent } from 'routes/Menu/MenuExperience/MenuPage/types'
import type {
  CampaignBannerHeaderContentBlock,
  CompactCampaignBannerHeaderContentBlock,
} from 'routes/Menu/MenuRecipesPage/CollectionHeader'
import { showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'
import { useBasket } from 'routes/Menu/domains/basket'
import { useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'

export type FeaturedRangeCampaignBannerProps = {
  merchandisingHeader: CampaignBannerHeaderContentBlock | CompactCampaignBannerHeaderContentBlock
}

const CampaignHeaderBox = withCitrusTheme(Box, () => ({
  [`@media (max-width: 543px)`]: {
    marginTop: '-1rem',
  },
  [`@media (min-width: 544px)`]: {
    marginLeft: 12,
    marginRight: 12,
  },
  [`@media (min-width: 768px)`]: {
    marginLeft: -30,
    marginRight: -30,
  },
}))

const getHeaderRecipeIds = (
  merchandisingHeader: FeaturedRangeCampaignBannerProps['merchandisingHeader'],
): RecipeUUID[] => {
  if (merchandisingHeader.type === 'campaign-banner') {
    return merchandisingHeader.items
      .filter(
        (
          item,
        ): item is Extract<CampaignBannerHeaderContentBlock['items'][number], { type: 'recipe' }> =>
          item.type === 'recipe',
      )
      .map((item) => item.id as RecipeUUID)
  }

  return merchandisingHeader.items.map((item) => item.id as RecipeUUID)
}

export const FeaturedRangeCampaignBanner = ({
  merchandisingHeader,
}: FeaturedRangeCampaignBannerProps) => {
  const dispatch = useDispatch()
  const { menuId } = useMenuMeta()
  const currentCollectionId = useCurrentCollectionId()
  const selectCollection = useSelectCollection()
  const collections = useCollections()
  const { addRecipe, removeRecipe, reachedLimit, canAddRecipes } = useBasket()
  const { basketHasRecipe } = useBasketRecipes()
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const recipes = useRecipes(getHeaderRecipeIds(merchandisingHeader))

  const recipeById = useMemo(
    () =>
      Object.fromEntries(
        recipes.flatMap((recipe) => (recipe ? [[recipe.id, recipe]] : [])),
      ) as Record<string, Recipe>,
    [recipes],
  )
  const collectionSlugById = useMemo(
    () =>
      Object.fromEntries(
        collections.map((collection) => [collection.id, collection.slug]),
      ) as Record<string, string>,
    [collections],
  )
  const currentCollectionSlug = collectionSlugById[currentCollectionId]

  const onRecipeClick = useCallback(
    (recipeId: string) => {
      dispatch(
        showDetailRecipe(recipeId, {
          source: 'merch_banner',
          merch_header_type: merchandisingHeader.type,
        }),
      )
      datadogRum?.addAction('open_menu_recipe_detail', {
        menu_id: menuId,
        recipe_id: recipeId,
      })
    },
    [dispatch, menuId, merchandisingHeader.type],
  )

  const onAddClick = useCallback(
    (recipeId: string) => {
      const recipe = recipeById[recipeId]

      if (recipe) {
        addRecipe(recipe.coreId, 'view_recipe_list_screen', {
          position: 'collection_merchandising_header',
          source: 'merch_banner',
          merch_header_type: merchandisingHeader.type,
        })
      }
    },
    [addRecipe, merchandisingHeader.type, recipeById],
  )

  const onRemoveClick = useCallback(
    (recipeId: string) => {
      const recipe = recipeById[recipeId]

      if (recipe) {
        removeRecipe(recipe.coreId, 'grid', 'collection_merchandising_header')
      }
    },
    [recipeById, removeRecipe],
  )

  const onCtaClick = useCallback(
    (collectionId: string) => {
      const collectionSlug = collectionSlugById[collectionId]

      if (collectionSlug) {
        queueTrackingEvent<SelectCollectionEvent>({
          action: 'select_collection',
          properties: {
            collection_id: collectionId as CollectionUUID,
            collection_slug:
              collectionSlug as SelectCollectionEvent['properties']['collection_slug'],
            current_collection_id:
              currentCollectionId as SelectCollectionEvent['properties']['current_collection_id'],
            ...(currentCollectionSlug && {
              current_collection_slug:
                currentCollectionSlug as SelectCollectionEvent['properties']['current_collection_slug'],
            }),
            merch_header_type: merchandisingHeader.type,
            navigation_method: 'merch_banner',
          },
        })
      }

      selectCollection(collectionId as CollectionUUID)
    },
    [
      collectionSlugById,
      currentCollectionSlug,
      currentCollectionId,
      merchandisingHeader.type,
      selectCollection,
    ],
  )

  const getRecipeCardState = useCallback(
    (recipeId: string) => {
      const recipe = recipeById[recipeId]
      const isInBasket = recipe ? basketHasRecipe(recipe.coreId) : false

      return {
        isInBasket,
        isOutOfStock: recipe ? !recipe.isAvailable && !isInBasket : false,
      }
    },
    [basketHasRecipe, recipeById],
  )

  return (
    <CampaignHeaderBox data-testid="featured-range-header-box">
      {(() => {
        switch (merchandisingHeader.type) {
          case 'campaign-banner':
            return (
              <CampaignBanner
                contentBlock={merchandisingHeader}
                getRecipeCardState={getRecipeCardState}
                canAddRecipes={canAddRecipes}
                reachedLimit={reachedLimit}
                onAddClick={onAddClick}
                onBlockedAddAttempt={setBrowserCTAVisible}
                onCtaClick={onCtaClick}
                onRecipeClick={onRecipeClick}
                onRemoveClick={onRemoveClick}
              />
            )
          case 'compact-campaign-banner':
            return (
              <CompactCampaignBanner
                contentBlock={merchandisingHeader}
                getRecipeCardState={getRecipeCardState}
                canAddRecipes={canAddRecipes}
                reachedLimit={reachedLimit}
                onAddClick={onAddClick}
                onBlockedAddAttempt={setBrowserCTAVisible}
                onCtaClick={onCtaClick}
                onRecipeClick={onRecipeClick}
                onRemoveClick={onRemoveClick}
              />
            )
          default:
            return null
        }
      })()}
    </CampaignHeaderBox>
  )
}
